import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '@/lib/api';

const downloadCsv = async () => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/analytics.csv`, { credentials: 'include' });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'fairway-trends.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const Bars = ({ items, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.raw ?? i.value));
  return (
    <div className="analytics-bars" data-testid={testid}>
      {items.length === 0 && <p className="muted">No data yet.</p>}
      {items.map((i) => (
        <div className="analytics-row" key={i.label}>
          <span className="analytics-label">{i.label}</span>
          <div className="analytics-track"><div className="analytics-fill" style={{ width: `${((i.raw ?? i.value) / max) * 100}%` }} /></div>
          <strong>{i.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api('/analytics/trends').then(setData); }, []);

  if (!data) return <p className="muted">Loading trend data…</p>;

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">TREND ANALYSIS</span>
          <h1>Insights from cases</h1>
          <p className="muted">Drawn directly from your case register — filter, learn, act.</p>
        </div>
        <button data-testid="export-analytics-csv" className="secondary-btn" onClick={downloadCsv}>
          <Download size={15} /> Export CSV
        </button>
      </div>
      <div className="metric-grid">
        <div className="metric" data-testid="analytics-total"><span>Total cases</span><strong>{data.total_cases}</strong><small>All time</small></div>
        <div className="metric" data-testid="analytics-resolved"><span>Resolved</span><strong>{data.resolved_cases}</strong><small>Closed with outcome</small></div>
        <div className="metric" data-testid="analytics-rate"><span>Resolution rate</span><strong>{data.resolution_rate}%</strong><small>Cases closed vs. opened</small></div>
        <div className="metric" data-testid="analytics-open"><span>Still open</span><strong>{data.total_cases - data.resolved_cases}</strong><small>Awaiting action</small></div>
      </div>
      <div className="overview-grid" style={{ marginTop: 14 }}>
        <section className="panel">
          <div className="panel-title"><div><span className="eyebrow">BY VESSEL</span><h2>Cases per vessel</h2></div></div>
          <Bars items={data.by_vessel} testid="bars-vessel" />
        </section>
        <section className="panel">
          <div className="panel-title"><div><span className="eyebrow">BY CATEGORY</span><h2>Cases by category</h2></div></div>
          <Bars items={data.by_category} testid="bars-category" />
        </section>
      </div>
      <div className="overview-grid" style={{ marginTop: 14 }}>
        <section className="panel">
          <div className="panel-title"><div><span className="eyebrow">BY SEVERITY</span><h2>Cases by severity</h2></div></div>
          <Bars items={data.by_severity} testid="bars-severity" />
        </section>
        <section className="panel">
          <div className="panel-title"><div><span className="eyebrow">BY STATUS</span><h2>Cases by status</h2></div></div>
          <Bars items={data.by_status} testid="bars-status" />
        </section>
      </div>
      <div className="overview-grid" style={{ marginTop: 14 }}>
        <section className="panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">RESOLUTION TIME · LAST 6 MONTHS</span>
              <h2>Avg days to resolve · by vessel</h2>
            </div>
            <span className="live-dot">{data.resolved_last_180d} resolved</span>
          </div>
          {data.resolution_time_by_vessel.length === 0
            ? <p className="muted" style={{ marginTop: 14 }}>No resolved cases in the last 6 months yet.</p>
            : <Bars items={data.resolution_time_by_vessel.map((r) => ({ label: `${r.label} (${r.count})`, value: `${r.value}d`, raw: r.value }))} testid="bars-resolution-vessel" />}
        </section>
        <section className="panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">RESOLUTION TIME · LAST 6 MONTHS</span>
              <h2>Avg days to resolve · by category</h2>
            </div>
          </div>
          {data.resolution_time_by_category.length === 0
            ? <p className="muted" style={{ marginTop: 14 }}>No resolved cases in the last 6 months yet.</p>
            : <Bars items={data.resolution_time_by_category.map((r) => ({ label: `${r.label} (${r.count})`, value: `${r.value}d`, raw: r.value }))} testid="bars-resolution-category" />}
        </section>
      </div>
    </>
  );
}
